<template>
    <div :class="['dynamic-form-start', { 'dynamic-form-start--fixed-subject': fixedSubject }]">
        <h4 v-if="!fixedSubject">Client Forms:</h4>
        <div class="q-gutter-md">
            <div :class="['form-card', { 'form-card--fixed-subject': fixedSubject }]">
                <FormSelector v-model:formId="formId" @update:form="selectedForm = $event" />


                <div v-if="supportsXLSX && !fixedSubject" class="row justify-between items-center q-mt-sm q-mb-sm hidden" >
                    <q-btn label="Upload responses from XLSX" size="14px" no-caps flat rounded outlined color="primary"
                        :loading="uploadingXLSX" @click="triggerXLSXUpload" />
                    <q-btn label="Download XLSX template" size="14px" no-caps flat rounded outlined color="secondary"
                        :loading="downloadingXLSX" @click="downloadXLSXTemplate" />
                    <input ref="xlsxInput" type="file" accept=".xlsx" class="hidden" @change="onXLSXFileSelected" />
                </div>

                <div v-if="fixedSubject" class="q-mt-md">
                    <p class="q-mb-xs"><strong>Person</strong></p>
                    <q-banner rounded class="bg-grey-2 text-grey-9">
                        {{ fixedSubject.name }}
                    </q-banner>
                    <div v-if="formId" class="q-mt-md">
                        <span><strong>Previous Responses:</strong></span>
                        <PreviousFormsTable
                            ref="previousFormsTable"
                            :subject="subject"
                            :selectedFormId="formId"
                            :allow-assign="false"
                            @form-selected="openExistingForm"
                        />
                    </div>
                </div>

                <template v-else>
                    <q-toggle v-model="isInternalClient" label="Use Te Kohinga Person" color="primary" class="q-mt-sm q-mb-sm" />

                    <span v-if="isInternalClient">
                        <TkPersonSelect
                            v-model="selectedTkPerson"
                            @linked-to-paua="onTkPersonLinked"
                        />
                        <div v-if="selectedTkPerson && activeEngagementOptions.length" class="q-mt-md">
                            <p class="q-mb-sm"><strong>Link this form to a client engagement</strong></p>
                            <q-select
                                v-model="selectedClientEngagementId"
                                :options="activeEngagementOptions"
                                label="Optional client engagement"
                                outlined
                                dense
                                clearable
                                emit-value
                                map-options
                                :loading="loadingEngagements"
                            />
                        </div>
                        <div v-if="selectedTkPerson" class="q-mt-md">
                            <span><strong>Previous Te Kohinga Person Responses:</strong></span>
                            <PreviousFormsTable
                                ref="previousFormsTable"
                                :subject="historySubject"
                                :selectedFormId="formId"
                                :highlight-client-engagement-id="selectedClientEngagementId"
                                @form-selected="openExistingForm"
                                @assign-client="openAssignModal"
                            />
                        </div>
                    </span>

                    <span v-else>
                        <ClientSelect v-model="selectedClient" />
                        <div v-if="selectedClient">
                            <span><strong>Previous Client Responses:</strong></span>
                            <PreviousFormsTable
                                ref="previousFormsTable"
                                :subject="subject"
                                :selectedFormId="formId"
                                @form-selected="openExistingForm"
                            />
                        </div>
                    </span>
                </template>

                <div class="row justify-between items-center q-mt-md" style="gap: 8px;">
                    <span class="inline-flex">
                        <q-btn v-if="selectedForm?.versionStatus === 'current'" :disabled="disableExternalSend" @click="showExternalInviteDialog=true" label="Send to client email" size="16px" no-caps flat
                        rounded outlined color="secondary" icon="o_email" />
                        <q-tooltip v-if="externalSendDisabledReason" class="text-body2">
                            {{ externalSendDisabledReason }}
                        </q-tooltip>
                    </span>
                    <q-btn v-if="selectedForm?.versionStatus === 'current'" :disabled="disableSubmit" @click="showConsentDialog=true" label="Start new form" size="16px" no-caps flat
                    rounded outlined color="primary" icon="o_play_arrow" />
                </div>
            </div>
        </div>

        <ClientAssignDialog
            v-model:show="assignModalOpen"
            :subject="assignSubject"
            :responseId="assignResponseId"
            @assign="onAssignTemporaryResponses"
        />
        <FormConsentDialog v-model:show="showConsentDialog" @consent-given="openNewForm" />
        <ExternalFormInviteDialog
            v-model:show="showExternalInviteDialog"
            :form="selectedForm"
            :subject="subject"
            @sent="onExternalInvitationSent"
        />

    </div>
</template>

<script>
    import ClientSelect from "@/components/ClientComponents/ClientSelectComponent.vue";
    import FormSelector from "./FormSelector.vue";
    import PreviousFormsTable from "./PreviousFormsTable.vue";
    import FormConsentDialog from "./FormConsentDialog.vue";
    import TkPersonSelect from "@/components/ClientComponents/TemporaryClients/TkPersonSelect.vue";
    import ClientAssignDialog from "./ClientAssignDialog.vue";
    import ExternalFormInviteDialog from "./ExternalFormInviteDialog.vue";
    import api from "@/services/api/api";
    import notify from "@/services/util/notify";
    import { isValidEmail, pauaSubject, subjectWithClientEngagement, tkPersonSubject } from "@/services/util/formSubject";

    export default {
        name: "FormStartComponent",
        emits: ['open-form'],
        components: { ClientSelect, FormSelector, PreviousFormsTable, FormConsentDialog, TkPersonSelect, ClientAssignDialog, ExternalFormInviteDialog },
        props: {
            fixedSubject: {
                type: [Object, null],
                default: null
            }
        },
        data() {
            return {
                formId: null,
                selectedForm: null,

                selectedClient: null,
                selectedTkPerson: null,
                isInternalClient: false,
                activeEngagements: [],
                selectedClientEngagementId: null,
                loadingEngagements: false,

                showConsentDialog: false,
                showExternalInviteDialog: false,

                assignModalOpen: false,
                assignSubject: null,
                assignResponseId: null,

                uploadingXLSX: false,
                downloadingXLSX: false,
            }
        },
        computed: {
            // The currently-selected form subject (Paua client or tk_people record).
            baseSubject() {
                if (this.fixedSubject) return this.fixedSubject;
                if (this.isInternalClient) {
                    return this.selectedTkPerson ? tkPersonSubject(this.selectedTkPerson) : null;
                }
                return this.selectedClient ? pauaSubject(this.selectedClient) : null;
            },
            subject() {
                if (this.fixedSubject) return this.fixedSubject;
                return this.isInternalClient
                    ? subjectWithClientEngagement(this.baseSubject, this.selectedClientEngagementId)
                    : this.baseSubject;
            },
            historySubject() {
                return this.fixedSubject ? this.fixedSubject : this.baseSubject;
            },
            disableSubmit() {
                return this.formId === null || this.subject === null || this.selectedForm?.versionStatus !== 'current';
            },
            disableExternalSend() {
                return this.externalSendDisabledReason !== null;
            },
            externalSendDisabledReason() {
                if (this.formId === null) return "Select a form before sending it to a client.";
                if (this.selectedForm?.versionStatus !== 'current') return "No current version is available.";
                if (this.subject === null) return "Select a client before sending the form.";
                if (!['paua', 'tk_person'].includes(this.subject.source)) {
                    return "This client cannot receive forms by email.";
                }
                if (!this.subject.email) return "This client does not have an associated email address.";
                if (!isValidEmail(this.subject.email)) return "This client does not have a valid email address.";
                return null;
            },
            // Whether the selected form supports XLSX bulk import (forms.xlsx_import).
            supportsXLSX() {
                return !!this.selectedForm?.xlsxImport && this.selectedForm?.versionStatus === 'current';
            },
            activeEngagementOptions() {
                return this.activeEngagements.map(engagement => ({
                    value: engagement.id,
                    label: [
                        engagement.service_name || engagement.service_code,
                        engagement.status_name || engagement.status_code,
                        this.formatEngagementDate(engagement.entered_at),
                    ].filter(Boolean).join(" - "),
                }));
            }
        },
        watch: {
            selectedTkPerson() {
                this.selectedClientEngagementId = null;
                this.activeEngagements = [];
                this.fetchActiveEngagements();
            }
        },
        methods: {
            onExternalInvitationSent(invitation) {
                notify.primary(`Form sent to ${invitation.recipientEmail}.`, "top");
                this.$refs.previousFormsTable?.fetchPreviousForms();
            },
            async fetchActiveEngagements() {
                if (!this.selectedTkPerson?.id) return;
                this.loadingEngagements = true;
                try {
                    const res = await api.tkPeople.listEngagements(this.selectedTkPerson.id, { status: "active" });
                    this.activeEngagements = res.data || [];
                } catch (error) {
                    notify.error("Failed to load active client engagements.", "top");
                } finally {
                    this.loadingEngagements = false;
                }
            },
            formatEngagementDate(value) {
                if (!value) return null;
                const date = new Date(value);
                if (isNaN(date.getTime())) return null;
                return date.toLocaleDateString("en-nz");
            },
            // Download the bulk-entry XLSX template for the selected form.
            async downloadXLSXTemplate() {
                if (this.formId === null) return;
                this.downloadingXLSX = true;
                try {
                    await api.forms.downloadXlsxTemplate(this.formId, this.selectedForm?.title);
                } catch (error) {
                    notify.error("Failed to download XLSX template. Please try again.");
                } finally {
                    this.downloadingXLSX = false;
                }
            },
            // Open the OS file picker for selecting an XLSX of responses to upload.
            // Imports are anonymous, so no client needs to be selected first.
            triggerXLSXUpload() {
                if (this.formId === null) {
                    notify.error("Select a form before uploading responses.");
                    return;
                }
                this.$refs.xlsxInput?.click();
            },
            // Upload, parse, and synchronously save the workbook in one request.
            async onXLSXFileSelected(event) {
                const file = event.target.files?.[0];
                event.target.value = ""; // reset so re-selecting the same file fires change
                if (!file) return;

                this.uploadingXLSX = true;
                try {
                    const res = await api.forms.importXlsxResponses(this.formId, file);
                    const { imported = 0, skipped = 0, failed = 0 } = res.data ?? {};
                    if (failed > 0) {
                        notify.primary(`Imported ${imported}; ${skipped} skipped, ${failed} failed.`);
                    } else {
                        notify.primary(`Imported ${imported} response(s).`);
                    }
                    this.$refs.previousFormsTable?.fetchPreviousForms();
                } catch (error) {
                    notify.error("Failed to import the XLSX file. Please check it matches the template.");
                } finally {
                    this.uploadingXLSX = false;
                }
            },
            openAssignModal(formResponseId) {
                this.assignResponseId = formResponseId;
                this.assignSubject = this.historySubject;
                this.assignModalOpen = true;
            },
            // Assign selected internal/temporary client responses to the chosen Paua client
            async onAssignTemporaryResponses({ client, responseIds }) {
                try {
                    await api.forms.assignToClient(client, responseIds);
                    notify.primary("Responses successfully assigned to Paua client.");
                    this.assignModalOpen = false;
                    this.$refs.previousFormsTable?.fetchPreviousForms();
                } catch (error) {
                    notify.error("Failed to assign responses to Paua client. Please try again.");
                }
            },
            // After mapping a tk_person to a Paua client, switch the widget over
            // so the user continues working with the (now-linked) Paua client.
            onTkPersonLinked({ client }) {
                this.selectedTkPerson = null;
                this.selectedClient = client;
                this.isInternalClient = false;
            },
            openNewForm() {
                this.$emit('open-form', {
                    formId: this.formId,
                    subject: this.subject,
                });
            },
            openExistingForm(id, responseFormId) {
                this.$emit('open-form', {
                    formId: responseFormId || this.formId,
                    subject: this.historySubject,
                    responseId: id,
                });
            }
        }
    }
</script>

<style scoped>
.dynamic-form-start--fixed-subject {
    width: 100%;
}

.dynamic-form-start--fixed-subject .q-gutter-md,
.dynamic-form-start--fixed-subject .q-gutter-md > * {
    width: 100%;
    margin: 0;
}

.form-card--fixed-subject {
    width: 100%;
    max-width: none;
    padding: 0;
    border-radius: 0;
}

.form-card--fixed-subject .form-actions {
    justify-content: flex-end;
    margin-top: 18px;
}

.form-card--fixed-subject .form-actions .q-btn {
    min-width: 150px;
}
</style>
